import { useEffect, type ReactNode } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '@/auth/authConfig';
import '@/styles/screens.css';

/**
 * Gates the whole app behind Entra sign-in with NO visible login step: an
 * unauthenticated user is redirected straight to Microsoft (no button, no popup).
 * Access is still rejected without auth — you simply can't proceed past the
 * redirect, and API access is separately group-gated. A brief spinner shows while
 * MSAL processes the redirect.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch(console.error);
    }
  }, [isAuthenticated, inProgress, instance]);

  if (isAuthenticated) return <>{children}</>;

  // Not authenticated yet (or mid-redirect) — show a neutral spinner only.
  return (
    <div className="auth-splash">
      <span className="spin" aria-label="Signing in">⟳</span>
    </div>
  );
}
